import { useState, Fragment, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  Typography,
  Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PerfectScrollbarComponent from 'react-perfect-scrollbar';
import CustomChip from 'src/@core/components/mui/chip';
import CustomAvatar from 'src/@core/components/mui/avatar';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { axiosInstance } from 'src/network/adapter';
import { ApiEndPoints } from 'src/network/endpoints';
import { toastError } from 'src/utils/utils';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import UserIcon from 'src/layouts/components/UserIcon';

const Menu = styled(MuiMenu)(({ theme }) => ({
  '& .MuiMenu-paper': {
    width: 380,
    overflow: 'hidden',
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  '& .MuiMenu-list': {
    padding: 0,
  },
}));

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const styles = {
  maxHeight: 349,
  '& .MuiMenuItem-root:last-of-type': {
    border: 0,
  },
};

const PerfectScrollbar = styled(PerfectScrollbarComponent)({
  ...styles,
});

const Avatar = styled(CustomAvatar)({
  width: '2.375rem',
  height: '2.375rem',
  fontSize: '1.125rem',
});

const MenuItemTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  //flex: '1 1 100%',
  overflow: 'hidden',
  fontSize: '0.875rem',
  whiteSpace: 'nowrap',
  //textOverflow: 'ellipsis',
  marginBottom: theme.spacing(0.75),
}));

const MenuItemSubtitle = styled(Typography)({
  // flex: '1 1 100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textWrap: 'wrap',
  maxWidth: 220
  // textOverflow: 'ellipsis'
})

const NotificationDropdown = props => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notificationData, setNotificationData] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const handleNotificationNavigation = (notification) => {
    const { type, meta_data } = notification;
    
    // Mark notification as read first
    markAsRead(notification.id);
    handleDropdownClose();
    
    // Navigate based on notification type
    switch (type) {
      case 'ad_hoc_quote_accepted':
      case 'ad_hoc_quote_rejected':
        if (meta_data?.bookingId) {
          navigate(`/task-execution/task-adhoc-detail/${meta_data.bookingId}`);
        } else {
          navigate('/task-execution');
        }
        break;
        
      case 'new_booking_received':
      case 'booking_canceled':
      case 'booking_completed':
      case 'booking_reassigned':
        if (meta_data?.bookingId) {
          navigate(`/booking-requests/booking-detail/${meta_data.bookingId}`);
        } else {
          navigate('/booking-requests');
        }
        break;
        
      case 'staff_added':
        navigate('/staff-management');
        break;

      case 'mechanic_task_completed':
        if (meta_data?.bookingId) {
          navigate(`/job-archive/${meta_data.bookingId}`);
        } else {
          navigate('/job-archive');
        }
        break;

      case 'new_job_assigned':
      case 'mechanic_issue_accepted':
      case 'mechanic_issue_rejected':
        if (meta_data?.bookingId) {
          navigate(`/task-execution/task-basic-detail/${meta_data.bookingId}`);
        } else {
          navigate('/task-execution');
        }
        break;

        
        case 'chat' : navigate('/chat'); break;
      default:
        // For unknown types, navigate to general notification page
        navigate(`/notification-detail/${notification.id}`);
        break;
    }
  };

  const fetchData = () => {
    setLoading(true);
    axiosInstance
      .get(ApiEndPoints.NOTIFICATION.list)
      .then(response => {
        console.log('Notification API response:', response.data);
        if (response.data?.data?.notifications) {
          setNotificationData(response.data.data.notifications);
          setUnreadCount(response.data.data.unreadCount || 0);
        } else {
          setNotificationData([]);
          setUnreadCount(0);
        }
      })
      .catch(error => {
        console.error('Error fetching notifications:', error);
        toastError(error);
        setNotificationData([]);
        setUnreadCount(0);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // useEffect(() => {
  //   fetchData();
  // }, []);

  // Listen for custom event to refresh notifications
  useEffect(() => {
    const handleNotificationUpdate = () => {
      fetchData();
    };

    window.addEventListener('notifications-updated', handleNotificationUpdate);
    
    return () => {
      window.removeEventListener('notifications-updated', handleNotificationUpdate);
    };
  }, []);

  const markAsRead = (id) => {
    setLoading(true);
    axiosInstance
      .patch(ApiEndPoints.NOTIFICATION.markAsRead(id))
      .then((response) => {
        console.log('Notification marked as read:', response.data);
        fetchData();
      })
      .catch((error) => {
        console.error('Error marking notification as read:', error);
        toastError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const { settings } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const hidden = useMediaQuery(theme => theme.breakpoints.down('lg'));
  const { direction } = settings;

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const ScrollWrapper = ({ children }) => {
    if (hidden) {
      return <Box sx={{ ...styles, overflowY: 'auto', overflowX: 'hidden' }}>{children}</Box>;
    } else {
      return <PerfectScrollbar options={{ wheelPropagation: false, suppressScrollX: true }}>{children}</PerfectScrollbar>;
    }
  };

  return (
    <Fragment>
      <IconButton color='inherit' aria-haspopup='true' onClick={handleDropdownOpen} aria-controls='customized-menu'>
        <Badge badgeContent={unreadCount} color='secondary'>
          <NotificationsActiveIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'ltr' ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: direction === 'ltr' ? 'right' : 'left' }}
      >
        <MenuItem disableRipple>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ fontWeight: 600 }}>Notifications</Typography>
            <CustomChip
              skin='light'
              size='small'
              label={`${unreadCount} New`}
              color='primary'
              sx={{ height: 20, fontSize: '0.75rem', fontWeight: 500, borderRadius: '10px' }}
            />
          </Box>
        </MenuItem>

        <ScrollWrapper>
          {loading ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant='body2' color='secondary'>
                Loading notifications...
              </Typography>
            </Box>
          ) : notificationData.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant='body2' color='secondary'>
                No notifications yet
              </Typography>
            </Box>
          ) : (
            notificationData.map(notification => (
              <MenuItem 
                key={notification.id} 
                onClick={() => { 
                  handleNotificationNavigation(notification); 
                }}
                sx={{
                  backgroundColor: !notification.is_dealer_read ? '#f8f9fa' : 'transparent',
                  '&:hover': {
                    backgroundColor: !notification.is_dealer_read ? '#e9ecef' : '#f5f5f5',
                  }
                }}
              >
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                  <Avatar alt={notification.title}>
                    {notification.title ? notification.title.charAt(0).toUpperCase() : 'N'}
                  </Avatar>
                  <Box sx={{ mx: 4, flex: '1 1', display: 'flex', overflow: 'hidden', flexDirection: 'column' }}>
                    <MenuItemTitle sx={{ 
                      fontWeight: !notification.is_dealer_read ? 700 : 600,
                      color: !notification.is_dealer_read ? '#000' : 'inherit'
                    }}>
                      {notification.title}
                    </MenuItemTitle>
                    <MenuItemSubtitle variant='body2' sx={{
                      fontWeight: !notification.is_dealer_read ? 500 : 400,
                      color: !notification.is_dealer_read ? '#333' : '#666'
                    }}>
                      {notification.body}
                    </MenuItemSubtitle>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant='caption' sx={{ color: 'text.disabled' }}>
                      {moment(notification.created_at).format('DD-MM-YYYY')}
                    </Typography>
                    {!notification.is_dealer_read && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: '#25DB75',
                          mt: 0.5,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </MenuItem>
            ))
          )}
        </ScrollWrapper>
        {notificationData.length > 0 && (
          <MenuItem disableRipple sx={{ py: 3.5, borderBottom: 0, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
            <Button 
              variant='contained' 
              as={Link} 
              to='/notification' 
              onClick={handleDropdownClose} 
              sx={{ textDecoration: 'none', width: '100%', textAlign: 'center' }}
            >
              View All Notifications
            </Button>
          </MenuItem>
        )}
      </Menu>
    </Fragment>
  );
};

export default NotificationDropdown;
